import { useCallback, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronRight, Loader2, Printer, RefreshCw, Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PickListHeader } from '@/components/pick-lists/PickListHeader'
import { PickListTable } from '@/components/pick-lists/PickListTable'
import { printPickSheet } from '@/components/pick-lists/printPickSheet'
import {
  usePickList,
  useRecordPickListPrint,
  useRevisePickList,
  useUpdatePickListLine,
} from '@/hooks/usePickLists'
import { PERMISSIONS as P, usePermission } from '@/lib/permissions'
import { filterLines, groupByZone, tabCounts, type PickFilterTab } from '@/lib/pickList'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import type { PickListLine } from '@/types/pickList'

const TABS: { value: PickFilterTab; label: string }[] = [
  { value: 'all', label: 'All lines' },
  { value: 'reagent', label: 'Reagent' },
  { value: 'consumable', label: 'Consumable' },
  { value: 'kit', label: 'Kit' },
  { value: 'open', label: 'Open' },
  { value: 'exceptions', label: 'Exceptions' },
]

export default function PickListPage() {
  const { id } = useParams<{ id: string }>()
  const { data: pickList, isLoading, error } = usePickList(id)
  const revise = useRevisePickList(id ?? '')
  const updateLine = useUpdatePickListLine(id ?? '')
  const recordPrint = useRecordPickListPrint()
  const { can } = usePermission()
  const canGenerate = can(P.pickListsGenerate)

  const [tab, setTab] = useState<PickFilterTab>('all')
  const [query, setQuery] = useState('')
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  // Kept against the LINE rather than as a page banner: a failure belongs
  // beside the number that caused it, and a banner cannot say which row.
  const [pickError, setPickError] = useState<{ lineId: string; message: string } | null>(null)

  const counts = useMemo(() => tabCounts(pickList?.lines ?? []), [pickList?.lines])

  // Re-group after filtering rather than filtering the server's groups, so a
  // zone whose every line is filtered out disappears instead of showing empty.
  const zones = useMemo(() => {
    if (!pickList) return []
    return groupByZone(filterLines(pickList.lines, tab, query))
  }, [pickList, tab, query])

  /**
   * A closed sheet is a record of what happened, not a working document — the
   * server refuses line edits on one, and offering an input the server will
   * reject is worse than not offering it.
   */
  const editable =
    canGenerate && pickList?.status !== 'COMPLETED' && pickList?.status !== 'CANCELLED'

  const handlePickQty = useCallback(
    async (line: PickListLine, qtyPicked: number | null) => {
      setPickError(null)
      try {
        // All or nothing (z8nrz7fn5r): a step that refuses the pick refuses the
        // save, and the server's reason names the step.
        await updateLine.mutateAsync({ lineId: line.id, qtyPicked })
      } catch (err) {
        setPickError({
          lineId: line.id,
          message: apiErrorMessage(err, 'Could not record that quantity.'),
        })
      }
    },
    [updateLine],
  )

  const allCollapsed = zones.length > 0 && zones.every(z => collapsed.has(z.zoneCode))

  const toggleZone = (zoneCode: string) =>
    setCollapsed(prev => {
      const next = new Set(prev)
      if (next.has(zoneCode)) next.delete(zoneCode)
      else next.add(zoneCode)
      return next
    })

  const toggleAll = () =>
    setCollapsed(allCollapsed ? new Set() : new Set(zones.map(z => z.zoneCode)))

  const handlePrint = () => {
    if (!pickList) return
    printPickSheet(pickList)
    // Audit only — deliberately not awaited, so a slow write never delays the
    // print dialog.
    if (pickList.id) recordPrint.mutate(pickList.id)
  }

  if (isLoading) {
    return (
      <div className='flex items-center gap-2 p-8 text-sm text-muted-foreground'>
        <Loader2 className='h-4 w-4 animate-spin' />
        Loading pick list…
      </div>
    )
  }

  if (error || !pickList) {
    return (
      <div className='p-8'>
        <p className='text-sm text-destructive'>
          {error instanceof Error ? error.message : 'Pick list not found.'}
        </p>
        <Link to='/inventory/pick-lists' className='mt-2 inline-block text-sm underline'>
          Back to pick lists
        </Link>
      </div>
    )
  }

  return (
    <div className='space-y-3 p-4 md:p-6'>
      <nav className='flex items-center gap-1 text-xs text-muted-foreground'>
        <Link to='/inventory' className='hover:text-foreground'>
          Inventory
        </Link>
        <ChevronRight className='h-3 w-3' />
        <Link to='/inventory/pick-lists' className='hover:text-foreground'>
          Pick lists
        </Link>
        <ChevronRight className='h-3 w-3' />
        <span className='font-medium text-foreground'>{pickList.code}</span>
      </nav>

      <PickListHeader pickList={pickList} />

      {/* List-wide alerts — why a sheet is short or empty is never left implicit. */}
      {pickList.alerts.length > 0 && (
        <div className='rounded-lg border border-amber-200 bg-amber-50 p-3'>
          <ul className='space-y-1'>
            {pickList.alerts.map((a, i) => (
              <li key={i} className='text-xs font-medium text-amber-800'>
                {a.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Toolbar — pill filters on the left, actions on the right. Sits on the
          page background rather than in a card, per the spec. */}
      <div className='flex flex-wrap items-center gap-2'>
        <div className='flex flex-wrap items-center gap-1.5'>
          {TABS.map(t => (
            <button
              key={t.value}
              type='button'
              onClick={() => setTab(t.value)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                tab === t.value
                  ? 'border-[#14375e] bg-[#14375e] text-white'
                  : 'border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
            >
              {t.label}
              <span
                className={cn(
                  'tabular-nums',
                  tab === t.value ? 'text-white/70' : 'text-foreground/60',
                )}
              >
                {counts[t.value]}
              </span>
            </button>
          ))}
        </div>

        <div className='relative min-w-[16rem] flex-1'>
          <Search className='pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground' />
          <Input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder='Search item code, name, lot or location'
            className='h-8 pl-8 text-xs'
          />
        </div>

        <button
          type='button'
          onClick={toggleAll}
          className='shrink-0 text-xs font-medium text-muted-foreground hover:text-foreground'
        >
          {allCollapsed ? 'Expand all zones' : 'Collapse all zones'}
        </button>

        {canGenerate && (
          <Button
            size='sm'
            variant='outline'
            className='h-8 gap-1.5 text-xs'
            disabled={revise.isPending}
            onClick={() => revise.mutate()}
            title='Re-resolve demand and replace the lines'
          >
            {revise.isPending ? (
              <Loader2 className='h-3.5 w-3.5 animate-spin' />
            ) : (
              <RefreshCw className='h-3.5 w-3.5' />
            )}
            Revise
          </Button>
        )}

        <Button size='sm' variant='outline' className='h-8 gap-1.5 text-xs' onClick={handlePrint}>
          <Printer className='h-3.5 w-3.5' />
          Print pick sheet
        </Button>
        {/* "Complete pick list" belongs to the picking phase and is deliberately
            absent rather than rendered disabled — no dead controls. */}
      </div>

      {revise.error && (
        <p className='text-xs text-destructive'>
          {revise.error instanceof Error ? revise.error.message : 'Could not revise the list.'}
        </p>
      )}

      <PickListTable
        zones={zones}
        collapsed={collapsed}
        onToggleZone={toggleZone}
        // Undefined when the sheet cannot be worked, which is what makes the
        // cell render as text instead of a disabled input.
        onPickQty={editable ? handlePickQty : undefined}
        savingLineId={updateLine.isPending ? updateLine.variables?.lineId ?? null : null}
        pickError={pickError}
      />
    </div>
  )
}
